/**
 * Phase 18 — Enterprise Payment Readiness Evaluation Engine
 * Validates prerequisite conditions before authorizing an invoice for downstream settlement.
 */

import { InvoiceRecord } from '@/types/domain/invoice-management';

export interface ReadinessChecklistItem {
  id: string;
  label: string;
  passed: boolean;
  severity: 'BLOCKING' | 'WARNING';
  details: string;
}

export interface PaymentReadinessResult {
  isReadyForPayment: boolean;
  blockingReasons: string[];
  checklist: ReadinessChecklistItem[];
}

export function evaluatePaymentReadiness(invoice: InvoiceRecord): PaymentReadinessResult {
  const blockingReasons: string[] = [];
  const checklist: ReadinessChecklistItem[] = [];

  // Check 1: PO Valid
  const poPassed = Boolean(invoice.poId && invoice.poNumber);
  checklist.push({
    id: 'check-po',
    label: 'Authorized Purchase Order Commitment',
    passed: poPassed,
    severity: 'BLOCKING',
    details: poPassed
      ? `Referenced PO #${invoice.poNumber} verified in system.`
      : 'No valid Purchase Order associated with this invoice.',
  });
  if (!poPassed) blockingReasons.push('Missing valid Purchase Order commitment.');

  // Check 2: Goods Receipt / GRN Accepted
  const grnPassed = Boolean(invoice.grnId || invoice.grnNumber);
  checklist.push({
    id: 'check-grn',
    label: 'Warehouse Goods Receipt (GRN) Inwarding',
    passed: grnPassed,
    severity: 'BLOCKING',
    details: grnPassed
      ? `Verified against GRN #${invoice.grnNumber || invoice.grnId}.`
      : 'Missing warehouse receiving evidence (GRN). Goods must be inwarded prior to payment.',
  });
  if (!grnPassed) blockingReasons.push('Missing warehouse Goods Receipt Note (GRN).');

  // Check 3: 3-Way Match Clearance
  const matchPassed = invoice.matchingStatus === 'MATCHED';
  checklist.push({
    id: 'check-match',
    label: 'Automated 3-Way Financial Match',
    passed: matchPassed,
    severity: 'BLOCKING',
    details: matchPassed
      ? 'Clean 3-way match verified (PO vs GRN vs Invoice).'
      : `Matching status is ${invoice.matchingStatus.replace(/_/g, ' ')}. Variances must be reconciled.`,
  });
  if (!matchPassed) blockingReasons.push(`3-Way match not completed (${invoice.matchingStatus}).`);

  // Check 4: No Blocking or Critical Exceptions
  const unresolvedBlocking = invoice.exceptions.filter(
    (e) => (e.severity === 'BLOCKING' || e.severity === 'CRITICAL') && e.status !== 'RESOLVED'
  );
  const exceptionsPassed = unresolvedBlocking.length === 0;
  checklist.push({
    id: 'check-exceptions',
    label: 'Exception Resolution Clearance',
    passed: exceptionsPassed,
    severity: 'BLOCKING',
    details: exceptionsPassed
      ? 'Zero unresolved blocking exceptions.'
      : `${unresolvedBlocking.length} blocking exception(s) require formal finance override or resolution.`,
  });
  if (!exceptionsPassed) {
    unresolvedBlocking.forEach((e) => blockingReasons.push(`Unresolved exception: ${e.description}`));
  }

  // Check 5: Duplicate Risk Cleared
  const duplicatePassed = invoice.duplicateRisk === 'NO_DUPLICATE';
  checklist.push({
    id: 'check-duplicate',
    label: 'Duplicate Invoice Screening',
    passed: duplicatePassed,
    severity: 'BLOCKING',
    details: duplicatePassed
      ? 'Screened against 500+ records: No duplicate detected.'
      : `Duplicate alert flagged (${invoice.duplicateRisk}). Review required prior to disbursement.`,
  });
  if (!duplicatePassed) blockingReasons.push(`Duplicate risk flagged (${invoice.duplicateRisk}).`);

  // Check 6: Finance Approval Status
  const approvalPassed =
    invoice.status === 'APPROVED' ||
    invoice.status === 'READY_FOR_PAYMENT' ||
    invoice.status === 'SCHEDULED_FOR_PAYMENT' ||
    invoice.status === 'PAID';
  checklist.push({
    id: 'check-approval',
    label: 'Accounts Payable & Finance Authorization',
    passed: approvalPassed,
    severity: 'BLOCKING',
    details: approvalPassed
      ? 'Finance authorization granted.'
      : 'Invoice pending final AP review & approval.',
  });
  if (!approvalPassed) blockingReasons.push('Invoice has not received formal AP approval.');

  const isReadyForPayment = blockingReasons.length === 0;

  return {
    isReadyForPayment,
    blockingReasons,
    checklist,
  };
}
